import { Box, Button, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { electronics } from '../../../../utils/constants';
import { DistanceSensorIcon } from '../../../Icons/Bits';
import { DistanceBasedSensorCheckContent, useDistanceBasedSensorCheck } from './DistanceBasedSensorCheck';
import { useAppSelector } from '../../../../redux/ReduxHooks';
import { selectAreDrivebaseCommandActionsDisabled } from '../../../../redux/RobotSlice';

interface DistanceSensorCheckDialogProps {
  open: boolean;
  port?: number;
  onClose: () => void;
}

const DistanceSensorCheckDialog = ({ open, port, onClose }: DistanceSensorCheckDialogProps) => {
  const { DETECTION_RANGES } = electronics.distanceSensor;
  const commandActionsDisabled = useAppSelector(selectAreDrivebaseCommandActionsDisabled);

  const { beamBroken, distance, thresholdMeters, setThresholdMeters } = useDistanceBasedSensorCheck({
    sensorType: 'distance',
    port,
    open,
    disableWrites: commandActionsDisabled,
    detectionRanges: DETECTION_RANGES,
    pollSensor: (p) => window.robotService?.getDistanceSensorSettings?.(p),
    setSensorSettings: (p, threshold) => window.robotService?.setDistanceSensorSettings?.(p, threshold),
  });

  const handleClose = () => {
    onClose();
  };

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0}>
          <Box
            component={DistanceSensorIcon}
            aria-hidden
            sx={{
              width: 28,
              height: 28,
              display: 'block',
              color: 'primary.main',
              mr: 1,
            }}
          />
          <Box component="span" sx={{ ml: 0.5 }}>
            {port != null ? (
              <Trans i18nKey="distanceSensorCheckDialog.titleWithPort" values={{ port }}>
                Port {{ port }}: Distance Sensor
              </Trans>
            ) : (
              <Trans i18nKey="distanceSensorCheckDialog.title">Distance Sensor</Trans>
            )}
          </Box>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ width: 500, minHeight: 300 }}>
        <Stack spacing={2} alignItems="stretch">
          <Typography>
            <Trans i18nKey="distanceSensorCheckDialog.main-text">
              Review the live output from your distance sensor electronic to confirm functionality:
            </Trans>
          </Typography>
          <DistanceBasedSensorCheckContent
            beamBroken={beamBroken}
            distance={distance}
            thresholdMeters={thresholdMeters}
            disabled={commandActionsDisabled}
            onThresholdMetersChange={setThresholdMeters}
            onThresholdMetersCommitted={setThresholdMeters}
            showObjectDetected
            brokenTrueI18nKey="distanceSensorCheckDialog.beam-broken-true"
            notBrokenFalseI18nKey="distanceSensorCheckDialog.beam-broken-false"
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'flex-end' }}>
        <Button variant="contained" autoFocus onClick={handleClose}>
          <Trans i18nKey="distanceSensorCheckDialog.button.close">Close</Trans>
        </Button>
      </DialogActions>
    </>
  );
};

export default DistanceSensorCheckDialog;
